/**
 * ModuleStub — data-rich "coming soon" landing for un-built V3 modules.
 *
 * Instead of a mostly-empty placeholder, we render a full workspace preview
 * so users feel the platform has been actively building this module in
 * the background. Every stub gets:
 *   • Hero card with engine-colored branding + confidence chip
 *   • Build progress bar with target release
 *   • Skeleton "Live preview" widget (chart shimmer)
 *   • Activity feed showing what the AI has been doing for this module
 *   • Roadmap card with per-feature progress bars
 *   • Sample-data KPI mini tiles that hint at what the shipped module will look like
 */
import {
  Sparkles, ArrowUpRight, Cpu, Activity, Timer, Waypoints,
  Signal, LineChart, ArrowRight, CheckCircle2, Zap,
} from "lucide-react";
import { themeFor, ENGINE_COLORS } from "@/pages/v3/engineTheme";
import Sparkline, { seedSeries } from "@/pages/v3/Sparkline";
import DailyBusinessScore from "@/pages/v3/DailyBusinessScore";

// Map product-oriented status → chip label + accent
const STATUS_LABELS = {
  "preview":       { label: "Preview",       tone: "muted" },
  "beta":          { label: "Beta",          tone: "amber" },
  "early-access":  { label: "Early Access",  tone: "violet" },
  "generally-available": { label: "Live",    tone: "emerald" },
};

// Deterministic pseudo-random from a string
function seedRand(str) {
  let s = 0;
  for (const c of String(str)) s = (s * 31 + c.charCodeAt(0)) & 0xffff;
  return () => {
    s = (s * 9301 + 49297) & 0xffff;
    return (s % 1000) / 1000;
  };
}

export default function ModuleStub({
  icon: Icon,
  title,
  eyebrow,
  description,
  bullets = [],
  slug,
  status = "preview",   // "preview" | "beta" | "early-access" | "generally-available"
  testid,
}) {
  const theme = slug ? themeFor(slug) : ENGINE_COLORS.slate;
  const r = seedRand(slug || title || "stub");
  // Build progress + release date — stable per slug
  const buildPct = 40 + Math.round(r() * 45);
  const daysToBeta = 14 + Math.round(r() * 45);
  const betaDate = new Date();
  betaDate.setDate(betaDate.getDate() + daysToBeta);
  const betaLabel = betaDate.toLocaleDateString(undefined,
    { month: "short", day: "numeric", year: "numeric" });

  // Status label
  const statusInfo = STATUS_LABELS[status] || STATUS_LABELS["preview"];
  const eyebrowLabel = eyebrow || statusInfo.label;
  const eyebrowChip = eyebrow
    ? `${theme.chipBg} ${theme.chipText} ring-1 ${theme.chipRing}`
    : (statusInfo.tone === "amber"
        ? "bg-amber-500/10 text-amber-700 ring-1 ring-amber-500/30"
        : statusInfo.tone === "violet"
        ? "bg-violet-500/10 text-violet-700 ring-1 ring-violet-500/30"
        : statusInfo.tone === "emerald"
        ? "bg-emerald-500/10 text-emerald-700 ring-1 ring-emerald-500/30"
        : "bg-slate-500/10 text-slate-700 ring-1 ring-slate-500/30");

  // Roadmap items with per-item progress
  const roadmapItems = bullets.map((b) => ({
    label: b,
    pct: 20 + Math.round(r() * 70),
  }));

  // KPI mini tiles — 3 pseudo-metrics the finished module would surface
  const kpiTiles = kpisForSlug(slug, r);

  // Recent AI activity for this module (stable per slug)
  const activity = activityForSlug(slug, r);

  // Mini Daily Business Score for this module (deterministic per slug)
  const moduleScore = Math.round(58 + r() * 32);
  const moduleTrend = Math.round((r() - 0.35) * 8);

  return (
    <div data-testid={testid || "v3-module-stub"} className="max-w-6xl">
      {/* HERO */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white p-6 md:p-8 shadow-sm">
        {/* Branded background layers */}
        <div className="absolute inset-0 v3-hero-texture pointer-events-none" />
        <div className="absolute inset-0 v3-hero-noise pointer-events-none" />
        <div
          className="absolute inset-0 opacity-[0.35] pointer-events-none"
          style={{
            backgroundImage:
              `radial-gradient(ellipse at 0% 0%, ${theme.hex}18, transparent 55%), radial-gradient(ellipse at 100% 0%, ${theme.hex}10, transparent 55%), radial-gradient(ellipse at 100% 100%, ${theme.hex}08, transparent 55%)`,
          }}
        />
        <div className="absolute inset-0 bg-white/60 backdrop-blur-2xl pointer-events-none" />

        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div
              data-testid={`${testid || "stub"}-status-chip`}
              className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-black px-2.5 py-1 rounded-full ${eyebrowChip}`}
            >
              <Sparkles className="w-3 h-3" />
              {eyebrowLabel}
            </div>
            <div className="mt-5 flex items-start gap-4">
              {Icon && (
                <div className="h-14 w-14 rounded-2xl grid place-items-center text-white shadow-md shrink-0"
                     style={{ backgroundImage: `linear-gradient(135deg, ${theme.hex}, ${theme.hex}cc)` }}>
                  <Icon className="w-7 h-7" />
                </div>
              )}
              <div className="min-w-0">
                <h1 className="text-[32px] md:text-[38px] font-bold text-slate-900 tracking-tight leading-tight">
                  {title}
                </h1>
                {description && (
                  <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl text-[15px]">
                    {description}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-3">
            {/* Mini signature score for this module */}
            <div className="rounded-xl border border-slate-200 bg-white/60 backdrop-blur px-4 py-2.5"
                 data-testid={`${testid || "stub"}-module-score`}>
              <DailyBusinessScore
                score={moduleScore}
                trend={moduleTrend}
                size="sm"
                label="Module health"
                testid={`${testid || "stub"}-module-score-ring`}
              />
            </div>

            {/* Build status card */}
            <div className="rounded-xl border border-slate-200 bg-white/60 backdrop-blur p-4 min-w-[220px]"
                 data-testid={`${testid || "stub"}-build-status`}>
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                <Waypoints className="w-3 h-3" style={{ color: theme.hex }} />
                Build status
              </div>
              <div className="mt-1 text-[24px] font-bold text-slate-900 tabular-nums leading-none">
                {buildPct}%
                <span className="ml-1 text-[11px] font-semibold text-slate-500 align-top">complete</span>
              </div>
              <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full"
                     style={{
                       width: `${buildPct}%`,
                       backgroundImage: `linear-gradient(90deg, ${theme.hex}bb, ${theme.hex})`,
                     }} />
              </div>
              <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500">
                <Timer className="w-3 h-3" style={{ color: theme.hex }} />
                {statusInfo.label} target · <span className="font-semibold text-slate-700">{betaLabel}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPI mini-tiles (what the finished module will surface) */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
        {kpiTiles.map((k) => (
          <div key={k.label}
               data-testid={`${testid || "stub"}-preview-kpi-${k.slug}`}
               className="relative overflow-hidden rounded-xl border border-slate-200/70 bg-white p-4 shadow-sm">
            <div className="absolute -top-8 -right-8 w-20 h-20 rounded-full opacity-[0.08]"
                 style={{ backgroundColor: theme.hex }} />
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg grid place-items-center text-white shadow-sm"
                   style={{ backgroundImage: `linear-gradient(135deg, ${theme.hex}, ${theme.hex}cc)` }}>
                <k.icon className="w-3.5 h-3.5" />
              </div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 truncate">
                {k.label}
              </div>
            </div>
            <div className="mt-2 flex items-end justify-between gap-2">
              <div className="text-[20px] font-bold text-slate-900 tabular-nums">
                {k.value}
              </div>
              <Sparkline data={k.series} color={theme.hex} width={68} height={22} />
            </div>
            <div className="mt-1 text-[10px] text-slate-400">{k.hint}</div>
          </div>
        ))}
      </div>

      {/* Preview + Activity split */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Preview widget (skeleton chart) */}
        <div
          data-testid={`${testid || "stub"}-preview`}
          className="lg:col-span-2 rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg grid place-items-center text-white shadow-sm"
                   style={{ backgroundImage: `linear-gradient(135deg, ${theme.hex}, ${theme.hex}cc)` }}>
                <LineChart className="w-3.5 h-3.5" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  Live Preview
                </div>
                <div className="text-[14px] font-bold text-slate-900 leading-tight">
                  What the finished view will feel like
                </div>
              </div>
            </div>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
              <Signal className="w-3 h-3" /> Skeleton
            </span>
          </div>

          <SkeletonChart theme={theme} slug={slug} />

          <div className="mt-3 grid grid-cols-3 gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-lg border border-slate-100 p-2">
                <div className="h-2 w-14 v3-shimmer rounded-full" />
                <div className="mt-1.5 h-3.5 w-20 v3-shimmer rounded-md" />
              </div>
            ))}
          </div>
        </div>

        {/* Activity feed */}
        <div
          data-testid={`${testid || "stub"}-activity`}
          className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm"
        >
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg grid place-items-center text-white shadow-sm"
                 style={{ backgroundImage: "linear-gradient(135deg, #8B5CF6, #6D28D9)" }}>
              <Activity className="w-3.5 h-3.5" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                AI Activity
              </div>
              <div className="text-[14px] font-bold text-slate-900 leading-tight">
                Working in the background
              </div>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            {activity.map((a, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className="h-6 w-6 rounded-md grid place-items-center text-white shrink-0"
                     style={{ backgroundImage: `linear-gradient(135deg, ${a.color}, ${a.color}cc)` }}>
                  <a.icon className="w-3 h-3" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-semibold text-slate-900 leading-snug truncate">
                    {a.title}
                  </div>
                  <div className="text-[11px] text-slate-500 leading-snug truncate">
                    {a.body}
                  </div>
                </div>
                <div className="text-[10px] text-slate-400 whitespace-nowrap font-medium">
                  {a.time}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Roadmap */}
      {roadmapItems.length > 0 && (
        <div
          data-testid={`${testid || "stub"}-roadmap`}
          className="mt-4 rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm"
        >
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <Zap className={`w-3 h-3 ${theme.iconText}`} /> Roadmap · what&rsquo;s under construction
          </div>
          <ul className="mt-3 space-y-3.5">
            {roadmapItems.map((it, i) => {
              const done = it.pct >= 90;
              return (
                <li key={i} className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
                  <span className="h-5 w-5 rounded-full grid place-items-center shrink-0"
                        style={{ backgroundColor: done ? theme.hex : "#f1f5f9" }}>
                    {done
                      ? <CheckCircle2 className="w-3 h-3 text-white" />
                      : <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: theme.hex }} />}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[13px] text-slate-800">{it.label}</div>
                    <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500"
                           style={{
                             width: `${it.pct}%`,
                             backgroundImage: `linear-gradient(90deg, ${theme.hex}bb, ${theme.hex})`,
                           }} />
                    </div>
                  </div>
                  <div className="text-[11px] font-bold tabular-nums" style={{ color: theme.hex }}>
                    {it.pct}%
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        <div className="text-[12px] text-slate-400 flex items-center gap-1.5">
          <ArrowUpRight className="w-3 h-3" />
          {statusInfo.label} · full release at target above.
        </div>
        <button type="button"
                className="text-[12px] font-semibold hover:underline inline-flex items-center gap-1"
                style={{ color: theme.hex }}>
          Request early access <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Skeleton chart — engine-colored bars that shimmer, hinting at
// the real chart that will render here at launch.
// ─────────────────────────────────────────────────────────────
function SkeletonChart({ theme, slug }) {
  const r = seedRand(slug || "chart");
  const bars = Array.from({ length: 16 }, () => 0.35 + r() * 0.6);
  return (
    <div className="mt-4 h-40 flex items-end gap-1.5 px-1">
      {bars.map((h, i) => (
        <div
          key={i}
          className="flex-1 rounded-t-md relative overflow-hidden"
          style={{
            height: `${Math.round(h * 100)}%`,
            backgroundColor: `${theme.hex}22`,
          }}
        >
          <div
            className="absolute bottom-0 left-0 right-0 rounded-t-md"
            style={{
              height: "60%",
              backgroundImage: `linear-gradient(180deg, ${theme.hex}, ${theme.hex}88)`,
            }}
          />
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Per-slug flavor: KPI tiles and activity feed content
// ─────────────────────────────────────────────────────────────
function kpisForSlug(slug, r) {
  const base = [
    { slug: "sample-a", icon: LineChart, label: "Sample metric A",
      value: `${Math.round(60 + r() * 40)}`, series: seedSeries(80, `${slug}-a`), hint: "vs. last 7 days" },
    { slug: "sample-b", icon: Activity, label: "Sample metric B",
      value: `$${Math.round(20 + r() * 80)}K`, series: seedSeries(50, `${slug}-b`), hint: "rolling avg" },
    { slug: "sample-c", icon: Cpu, label: "Sample metric C",
      value: `${Math.round(70 + r() * 30)}%`, series: seedSeries(90, `${slug}-c`), hint: "AI confidence" },
  ];
  const overrides = {
    "wholesale-engine": [
      { slug: "mao",         icon: LineChart, label: "Avg MAO",
        value: "$212K",   series: seedSeries(212, "mao"),   hint: "past 30d" },
      { slug: "fee",         icon: Activity,  label: "Avg assignment fee",
        value: "$14.2K",  series: seedSeries(14, "fee"),    hint: "market benchmark" },
      { slug: "buyer-depth", icon: Cpu,       label: "Buyer depth",
        value: "4.3",     series: seedSeries(4, "bd"),      hint: "matches per deal" },
    ],
    "repair": [
      { slug: "avg-scope",  icon: LineChart, label: "Avg scope",
        value: "$38.4K", series: seedSeries(38, "scope"), hint: "past 30d" },
      { slug: "bid-delta",  icon: Activity,  label: "Bid delta",
        value: "±11%",   series: seedSeries(11, "bd2"),  hint: "vs estimate" },
      { slug: "confidence", icon: Cpu,       label: "Estimate confidence",
        value: "92%",    series: seedSeries(92, "conf"), hint: "region-trained" },
    ],
    "comps-arv": [
      { slug: "comps-pulled", icon: LineChart, label: "Comps pulled",
        value: "1,428", series: seedSeries(1428, "cp"),  hint: "this week" },
      { slug: "arv-band",     icon: Activity,  label: "ARV band",
        value: "±4.2%", series: seedSeries(4, "ab"),     hint: "80% CI" },
      { slug: "adjustments",  icon: Cpu,       label: "Adjustments",
        value: "12",    series: seedSeries(12, "adj"),   hint: "avg per comp" },
    ],
    "deal-score": [
      { slug: "avg-score", icon: LineChart, label: "Avg score",
        value: "68",    series: seedSeries(68, "as"),    hint: "portfolio-wide" },
      { slug: "top-decile", icon: Activity, label: "Top decile",
        value: "94+",   series: seedSeries(94, "td"),    hint: "≥ score 94" },
      { slug: "shift",     icon: Cpu,       label: "24h shift",
        value: "+3.2",  series: seedSeries(3, "sh"),     hint: "moving average" },
    ],
    "ai-recommendations": [
      { slug: "queued",  icon: LineChart, label: "Recs queued",
        value: "12",  series: seedSeries(12, "rq"),  hint: "actionable today" },
      { slug: "applied", icon: Activity,  label: "Applied",
        value: "34",  series: seedSeries(34, "ap"),  hint: "past 30d" },
      { slug: "impact",  icon: Cpu,       label: "Modeled impact",
        value: "$126K", series: seedSeries(126, "im"), hint: "projected uplift" },
    ],
    "wholesale": [
      { slug: "assignments", icon: LineChart, label: "Assignments",
        value: "18",   series: seedSeries(18, "asg"), hint: "in flight" },
      { slug: "avg-close",   icon: Activity,  label: "Avg close",
        value: "23d",  series: seedSeries(23, "ac"),  hint: "contract → close" },
      { slug: "buyer-hit",   icon: Cpu,       label: "Buyer hit rate",
        value: "62%",  series: seedSeries(62, "bh"),  hint: "responses / blast" },
    ],
    "fix-flip": [
      { slug: "active",     icon: LineChart, label: "Active projects",
        value: "7",     series: seedSeries(7, "prj"),  hint: "in rehab" },
      { slug: "sched-var",  icon: Activity,  label: "Schedule var",
        value: "+4d",   series: seedSeries(4, "sv"),   hint: "vs plan" },
      { slug: "budget-var", icon: Cpu,       label: "Budget var",
        value: "-2.1%", series: seedSeries(2, "bv"),   hint: "vs plan" },
    ],
    "brrrr": [
      { slug: "cash-flow", icon: LineChart, label: "Monthly cash flow",
        value: "$4.2K", series: seedSeries(4, "cf"), hint: "post-refi net" },
      { slug: "dscr",      icon: Activity,  label: "Avg DSCR",
        value: "1.38",  series: seedSeries(138, "ds"), hint: "portfolio-wide" },
      { slug: "occupancy", icon: Cpu,       label: "Occupancy",
        value: "94%",   series: seedSeries(94, "oc"), hint: "12-mo avg" },
    ],
    "creative-finance": [
      { slug: "structures", icon: LineChart, label: "Structures live",
        value: "9",     series: seedSeries(9, "st"),  hint: "seller/sub-to/wrap" },
      { slug: "on-time",    icon: Activity,  label: "On-time payments",
        value: "98%",   series: seedSeries(98, "ot"), hint: "12-mo history" },
      { slug: "avg-spread", icon: Cpu,       label: "Avg spread",
        value: "3.6%",  series: seedSeries(36, "sp"), hint: "note yield vs cost" },
    ],
  };
  return overrides[slug] || base;
}

function activityForSlug(slug, r) {
  const times = ["4m ago", "22m ago", "1h ago", "3h ago", "6h ago", "9h ago"];
  const shared = [
    { icon: Cpu,      color: "#8B5CF6", title: "AI batch job completed",
      body: `${100 + Math.round(r() * 400)} records processed`, time: times[0] },
    { icon: Activity, color: "#3B82F6", title: "Data refresh",
      body: `MLS + off-market snapshot for 4 metros`, time: times[1] },
    { icon: Signal,   color: "#F59E0B", title: "Threshold breach",
      body: `Score volatility > 8σ on 3 properties`, time: times[2] },
    { icon: Sparkles, color: "#10B981", title: "New insight surfaced",
      body: `Buyer heat concentration in Tampa MSA`, time: times[3] },
    { icon: Timer,    color: "#EF4444", title: "Deadline flag",
      body: `2 assignments expire within 72 hours`, time: times[4] },
  ];
  const overrides = {
    "wholesale-engine": [
      { icon: Cpu,      color: "#F59E0B", title: "MAO ladder recalculated",
        body: "1247 Maplewood · new MAO $232K (+$3K)", time: times[0] },
      { icon: Activity, color: "#3B82F6", title: "Buyer shortlist rebuilt",
        body: "17 cash buyers ranked for 123 Demo Lane", time: times[1] },
      { icon: Signal,   color: "#8B5CF6", title: "Assignment fee flag",
        body: "88 Oakridge fee < 60% of market median", time: times[2] },
      { icon: Sparkles, color: "#10B981", title: "Best-fit strategy",
        body: "Nashville metro: 62% deals fit wholesale", time: times[3] },
      { icon: Timer,    color: "#EF4444", title: "Contract expiring",
        body: "3 assignments due within 5 days", time: times[4] },
    ],
    "repair": [
      { icon: Cpu,      color: "#10B981", title: "Repair scope estimated",
        body: "1247 Maplewood · $37,400 · 92% confidence", time: times[0] },
      { icon: Activity, color: "#3B82F6", title: "Contractor bid ingested",
        body: "123 Demo Lane · 3 bids · Δ 14% vs estimate", time: times[1] },
      { icon: Signal,   color: "#F59E0B", title: "CAPEX detection",
        body: "88 Oakridge · roof + HVAC flagged", time: times[2] },
      { icon: Sparkles, color: "#8B5CF6", title: "Regional cost update",
        body: "Nashville drywall costs +6% MoM", time: times[3] },
      { icon: Timer,    color: "#EF4444", title: "Contractor overdue",
        body: "1247 Maplewood plumber past SLA", time: times[4] },
    ],
    "comps-arv": [
      { icon: Activity, color: "#3B82F6", title: "Comps set pulled",
        body: "88 Oakridge · 7 sold comps · ARV $312K", time: times[0] },
      { icon: Cpu,      color: "#8B5CF6", title: "Adjustments applied",
        body: "1247 Maplewood · 12 adjustments · ±4%", time: times[1] },
      { icon: Signal,   color: "#F59E0B", title: "Comp velocity slowing",
        body: "Austin DOM +18% vs 90-day baseline", time: times[2] },
      { icon: Sparkles, color: "#10B981", title: "ARV band refined",
        body: "123 Demo Lane · $415K–$438K (80% CI)", time: times[3] },
      { icon: Timer,    color: "#EF4444", title: "Stale comps flag",
        body: "3 properties · comps > 45 days old", time: times[4] },
    ],
    "deal-score": [
      { icon: Cpu,      color: "#EF4444", title: "Deal score refreshed",
        body: "1247 Maplewood · 77 (+4 vs yesterday)", time: times[0] },
      { icon: Activity, color: "#8B5CF6", title: "Score volatility spike",
        body: "3 properties moved > 8σ overnight", time: times[1] },
      { icon: Signal,   color: "#F59E0B", title: "New high-score deal",
        body: "45 Cedar Ln entered top decile (94)", time: times[2] },
      { icon: Sparkles, color: "#10B981", title: "Portfolio drift",
        body: "Avg score up 3.2 vs 7-day rolling", time: times[3] },
      { icon: Timer,    color: "#3B82F6", title: "Explainability updated",
        body: "1247 Maplewood · 'why 77' regenerated", time: times[4] },
    ],
    "ai-recommendations": [
      { icon: Sparkles, color: "#8B5CF6", title: "Coach recommendation",
        body: "Rebalance Nashville → Tampa (+$62K)", time: times[0] },
      { icon: Cpu,      color: "#F59E0B", title: "Impact model refreshed",
        body: "12 recs re-scored against new comps", time: times[1] },
      { icon: Activity, color: "#3B82F6", title: "One-click apply",
        body: "'Blast to top 20 buyers' queued", time: times[2] },
      { icon: Signal,   color: "#10B981", title: "Rec confidence up",
        body: "Wholesale exit avg confidence 91%", time: times[3] },
      { icon: Timer,    color: "#EF4444", title: "Expiring window",
        body: "2 recs expire before market shift", time: times[4] },
    ],
    "wholesale": [
      { icon: Cpu,      color: "#F59E0B", title: "Assignment drafted",
        body: "123 BP007 Test → assignment doc ready", time: times[0] },
      { icon: Activity, color: "#3B82F6", title: "Buyer blast sent",
        body: "17 buyers · 4 replies within 12 min", time: times[1] },
      { icon: Signal,   color: "#8B5CF6", title: "Title company handoff",
        body: "1247 Maplewood · docs uploaded", time: times[2] },
      { icon: Sparkles, color: "#10B981", title: "Close velocity",
        body: "Avg contract → close 23 days (best 90d)", time: times[3] },
      { icon: Timer,    color: "#EF4444", title: "Assignment expiring",
        body: "88 Oakridge · 3 days to sign", time: times[4] },
    ],
    "fix-flip": [
      { icon: Cpu,      color: "#10B981", title: "Rehab milestone",
        body: "1247 Maplewood · framing 87% complete", time: times[0] },
      { icon: Activity, color: "#F59E0B", title: "Vendor invoice",
        body: "Plumber #3 · $8,400 · 2% budget delta", time: times[1] },
      { icon: Signal,   color: "#EF4444", title: "Schedule variance",
        body: "3 projects · slip > 3 days vs plan", time: times[2] },
      { icon: Sparkles, color: "#8B5CF6", title: "Re-list pricing",
        body: "123 Demo Lane · $445K (velocity-tuned)", time: times[3] },
      { icon: Timer,    color: "#3B82F6", title: "Inspection due",
        body: "88 Oakridge · rough electric Tue 10am", time: times[4] },
    ],
    "brrrr": [
      { icon: Cpu,      color: "#3B82F6", title: "Cash flow refreshed",
        body: "Portfolio net $4.2K/mo · DSCR 1.38", time: times[0] },
      { icon: Activity, color: "#10B981", title: "Refi eligible",
        body: "2 properties clear LTV + seasoning", time: times[1] },
      { icon: Signal,   color: "#F59E0B", title: "Rent growth signal",
        body: "Tampa +4.1% YoY · above pro-forma", time: times[2] },
      { icon: Sparkles, color: "#8B5CF6", title: "Tenant screening",
        body: "45 Cedar Ln · 3 approved applicants", time: times[3] },
      { icon: Timer,    color: "#EF4444", title: "Occupancy dip",
        body: "88 Oakridge · vacant 6 days", time: times[4] },
    ],
    "creative-finance": [
      { icon: Cpu,      color: "#8B5CF6", title: "Structure drafted",
        body: "1247 Maplewood · sub-to · docs ready", time: times[0] },
      { icon: Activity, color: "#3B82F6", title: "ACH pull complete",
        body: "9 notes · $47,200 collected on time", time: times[1] },
      { icon: Signal,   color: "#F59E0B", title: "Novation opportunity",
        body: "88 Oakridge · seller open to wrap", time: times[2] },
      { icon: Sparkles, color: "#10B981", title: "Note yield",
        body: "Avg spread 3.6% · trending +0.2%", time: times[3] },
      { icon: Timer,    color: "#EF4444", title: "Late payment",
        body: "123 Demo Lane · 4 days past due", time: times[4] },
    ],
  };
  return overrides[slug] || shared;
}
